import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getUserById } from '../utils/searchService';
import { supabase } from './supabaseClient';
import BackIcon from '../assets/back.png';
import ShareIcon from '../assets/share.png';
import BottomNav from './BottomNav';
import './JourneyPage.css';
import { db } from '../firebase';
import { doc, getDoc, setDoc, collectionGroup, query, where, getDocs, collection, orderBy, limit } from 'firebase/firestore';
import { getAuthor, authorAvatarUrl } from '../utils/authorLookup';
import { getImageUrl, getThumbUrl, getAvatarUrl } from '../utils/imageUrls';
import { useAuth } from '../authContext';
import { getStage } from '../utils/evolveUtils';
import { notificationService } from '../utils/notificationService';
import TopMindsBadge from '../TopMindsBadge';

// Import Stage Icons
import SparkImg from "../assets/spark journey page.png";
import OrbitImg from "../assets/orbit journey page.png";
import GravityImg from "../assets/gravity journey page.png";
import NovaImg from "../assets/nova journey page.png";
import ConstellationImg from "../assets/constellation journey page.png";
import OrbitIcon from "../assets/orbit.png";

const MAX_NOTE_LENGTH = 160;
const COLLECTIONS = ["postedIdeas", "publicIdeas"];
const OWNER_FIELDS = ["ownerId", "userId", "uid"];

// Never show an email as a name
const nameOf = (u) => u?.authorName || u?.name || u?.username || 'User';

// Treats true / "yes" / "true" (any case) as on.
const isYes = (v) =>
    v === true || ["yes", "true"].includes(String(v ?? "").trim().toLowerCase());

const millis = (v) => {
    if (!v) return 0;
    if (typeof v.toMillis === 'function') return v.toMillis();
    const t = new Date(v).getTime();
    return Number.isNaN(t) ? 0 : t;
};

const getStageIcon = (stage) => {
    switch (stage) {
        case "Spark": return SparkImg;
        case "Orbit": return OrbitImg;
        case "Gravity": return GravityImg;
        case "Nova": return NovaImg;
        case "Constellation": return ConstellationImg;
        default: return SparkImg;
    }
};

// Adds the instant public image URLs + stage icon to an idea object
const decorate = (idea) => ({
    ...idea,
    imageUrl: getImageUrl(idea.imagePath),   // full size (detail page)
    thumbUrl: getThumbUrl(idea.imagePath),   // small (cards on this page)
    stageIcon: getStageIcon(getStage(idea.evolveCount || 0)),
});

const fetchLatestEvolve = async (ideaId, collName) => {
    try {
        const q = query(
            collection(db, collName, ideaId, 'evolves'),
            orderBy('createdAt', 'desc'),
            limit(1)
        );
        const snap = await getDocs(q);
        if (!snap.empty) return { ...snap.docs[0].data(), id: snap.docs[0].id };
    } catch (e) { }
    return null;
};

const JourneyPage = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [user, setUser] = useState(null);
    const [createdIdeas, setCreatedIdeas] = useState([]);   // "All" tab (loaded up front)
    const [impressedIdeas, setImpressedIdeas] = useState([]); // loaded when the tab is first opened
    const [evolvedIdeas, setEvolvedIdeas] = useState([]);     // loaded when the tab is first opened
    const [tabLoading, setTabLoading] = useState({});
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('all');
    const loadedTabs = useRef({});

    const [profileImageUrl, setProfileImageUrl] = useState(null);
    const [expandedNoteIds, setExpandedNoteIds] = useState(new Set());

    const { currentUser, loading: authLoading } = useAuth();
    const [isEditingBio, setIsEditingBio] = useState(false);
    const [bioText, setBioText] = useState("");

    // Proud Moments (set manually on the user doc in Firestore)
    const [showProud, setShowProud] = useState(false);
    const [hasTopMinds, setHasTopMinds] = useState(false);

    const toggleNoteExpanded = (id) => {
        setExpandedNoteIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    useEffect(() => {
        if (!authLoading && !currentUser) {
            navigate('/Login_page', { state: { from: location.pathname } });
        }
    }, [currentUser, authLoading, navigate, location]);

    const saveBio = async () => {
        if (!userId) return;
        try {
            const userRef = doc(db, 'users', userId);
            await setDoc(userRef, { bio: bioText }, { merge: true });
            setIsEditingBio(false);
            setUser(prev => ({ ...prev, bio: bioText }));
        } catch (error) {
            console.error("Error saving bio:", error);
            alert("Failed to save bio.");
        }
    };

    // ---------- Main load: profile + the user's own ideas ("All" tab) ----------
    useEffect(() => {
        if (!userId) return undefined;
        let cancelled = false;

        const fetchJourneyData = async () => {
            try {
                setLoading(true);
                loadedTabs.current = {};
                setImpressedIdeas([]);
                setEvolvedIdeas([]);
                setCreatedIdeas([]);
                setProfileImageUrl(null);
                setShowProud(false);
                setHasTopMinds(false);

                // Proud Moments flags: runs alongside the rest, never blocks the page
                getDoc(doc(db, 'users', userId))
                    .then((snap) => {
                        if (cancelled) return;
                        const d = snap.exists() ? snap.data() : {};
                        setShowProud(isYes(d.proudMoments));
                        setHasTopMinds(isYes(d.topMindsAward));
                    })
                    .catch((e) => console.error('Proud moments load error', e));

                const userData = await getUserById(userId);
                if (cancelled) return;
                if (!userData) {
                    setUser(null);
                    return;
                }
                setUser(userData);
                setBioText(userData.bio || "");

                // Avatar (public URL, no signing)
                if (userData.profileImage) {
                    setProfileImageUrl(getAvatarUrl(userData.profileImage));
                } else {
                    try {
                        const { data: files } = await supabase.storage.from('images').list(`profile-pics/${userId}`, { limit: 1, sortBy: { column: 'created_at', order: 'desc' } });
                        if (!cancelled && files && files.length > 0) {
                            setProfileImageUrl(getAvatarUrl(`profile-pics/${userId}/${files[0].name}`));
                        }
                    } catch (e) { console.error(e); }
                }

                // The user's ideas: same collections and owner fields as before
                const created = [];
                try {
                    const seenIds = new Set();
                    const perCollection = await Promise.all(COLLECTIONS.map(async (coll) => {
                        const snaps = await Promise.all(
                            OWNER_FIELDS.map((field) => getDocs(query(collection(db, coll), where(field, '==', userId))))
                        );
                        return { coll, snaps };
                    }));
                    perCollection.forEach(({ coll, snaps }) => {
                        snaps.forEach((s) => {
                            s.docs.forEach((d) => {
                                if (seenIds.has(d.id)) return;
                                seenIds.add(d.id);
                                created.push({ ...d.data(), id: d.id, _coll: coll });
                            });
                        });
                    });
                } catch (e) { console.error("Creations fetch error:", e); }

                const decorated = created
                    .map(decorate)
                    .sort((a, b) => millis(b.createdAt) - millis(a.createdAt)); // newest first

                if (!cancelled) setCreatedIdeas(decorated);
            } catch (error) {
                console.error('Error fetching journey data:', error);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchJourneyData();
        return () => { cancelled = true; };
    }, [userId]);

    // ---------- Impressed tab: loaded the first time it is opened ----------
    const loadImpressed = async () => {
        try {
            const withEvolves = createdIdeas.filter((idea) => (idea.evolveCount || 0) > 0);
            const result = await Promise.all(withEvolves.map(async (idea) => {
                const latest = await fetchLatestEvolve(idea.id, idea._coll);
                if (!latest) return idea;
                const evolverId = latest.userId || latest.uid;
                const author = await getAuthor(evolverId); // shared + cached
                return {
                    ...idea,
                    latestEvolution: {
                        text: latest.text,
                        stage: latest.stage || 'Spark',
                        evolverName: (author && author.name) || 'User',
                        evolverImage: authorAvatarUrl(author),
                        userId: evolverId
                    }
                };
            }));
            setImpressedIdeas(result);
        } catch (e) {
            console.error("Impressed load error:", e);
        } finally {
            setTabLoading((p) => ({ ...p, impressed: false }));
        }
    };

    // ---------- Evolved tab: loaded the first time it is opened ----------
    const loadEvolved = async () => {
        try {
            const q1 = query(collectionGroup(db, 'evolves'), where('userId', '==', userId));
            const q2 = query(collectionGroup(db, 'evolves'), where('uid', '==', userId));
            const [s1, s2] = await Promise.all([getDocs(q1), getDocs(q2)]);

            // Both queries can return the same evolve; keep one per idea
            const byIdea = new Map();
            [...s1.docs, ...s2.docs].forEach((evolveDoc) => {
                const ideaRef = evolveDoc.ref.parent.parent;
                if (!ideaRef) return;
                if (!byIdea.has(ideaRef.path)) byIdea.set(ideaRef.path, { ideaRef, evolveDoc });
            });

            const list = (await Promise.all([...byIdea.values()].map(async ({ ideaRef, evolveDoc }) => {
                const ideaSnap = await getDoc(ideaRef);
                if (!ideaSnap.exists()) return null;

                const ideaData = ideaSnap.data();
                const owner = ideaData.ownerId || ideaData.userId || ideaData.uid;
                if (owner === userId) return null;

                const ev = evolveDoc.data();
                return {
                    ...decorate({ ...ideaData, id: ideaSnap.id }),
                    isEvolvedByMe: true,
                    myEvolveText: ev.text,
                    myEvolveStage: ev.stage,
                    evolvedAt: ev.createdAt
                };
            }))).filter(Boolean);

            list.sort((a, b) => millis(b.evolvedAt) - millis(a.evolvedAt));
            setEvolvedIdeas(list);
        } catch (e) {
            console.error("Evolved fetch error (possibly missing index):", e);
        } finally {
            setTabLoading((p) => ({ ...p, evolved: false }));
        }
    };

    useEffect(() => {
        if (loading || !user) return;
        if (activeTab === 'impressed' && !loadedTabs.current.impressed) {
            loadedTabs.current.impressed = true;
            loadImpressed();
        } else if (activeTab === 'evolved' && !loadedTabs.current.evolved) {
            loadedTabs.current.evolved = true;
            loadEvolved();
        }

    }, [activeTab, loading, user]);

    const selectTab = (tab) => {
        if (tab !== 'all' && !loadedTabs.current[tab]) {
            setTabLoading((p) => ({ ...p, [tab]: true }));
        }
        setActiveTab(tab);
    };

    const handleIdeaClick = (idea) => {
        navigate(`/view-idea/${idea.id}`, { state: { idea } });
    };

    const handleShare = () => {
        const url = window.location.origin + "/journey/" + userId;
        const shareName = user?.authorName || user?.name || user?.username || 'this profile';
        if (navigator.share) {
            navigator.share({
                title: `Check out ${shareName} on Trescend`,
                url: url
            }).catch(err => console.error("Share failed", err));
        } else {
            navigator.clipboard.writeText(url);
            notificationService.showSuccess("Profile link copied to clipboard!");
        }
    };

    const getDisplayIdeas = () => {
        switch (activeTab) {
            case 'impressed': return impressedIdeas;
            case 'evolved': return evolvedIdeas;
            case 'all': default: return createdIdeas;
        }
    };

    // ---------- LOADING SKELETON ----------
    if (loading) {
        return (
            <div className="journey-page">
                <div className="journey-header">
                    <img src={BackIcon} alt="Back" className="journey-back-btn" onClick={() => navigate(-1)} />
                    <h1 className="journey-title">Journey</h1>
                    <span className="journey-header-spacer" aria-hidden="true" />
                </div>
                <div className="journey-profile-section">
                    <div className="journey-profile-row">
                        <div className="journey-skel journey-skel-avatar" />
                        <div className="journey-profile-info">
                            <div className="journey-skel journey-skel-line" style={{ width: '55%', height: '16px', marginBottom: '10px' }} />
                            <div className="journey-skel journey-skel-pill" />
                        </div>
                    </div>
                    <div className="journey-skel journey-skel-bio" />
                </div>
                <div className="journey-ideas-grid">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="journey-skel-card">
                            <div className="journey-skel journey-skel-card-img" />
                            <div className="journey-skel journey-skel-line" style={{ width: '80%', height: '12px', margin: '10px 12px 6px' }} />
                            <div className="journey-skel journey-skel-line" style={{ width: '50%', height: '10px', margin: '0 12px 10px' }} />
                        </div>
                    ))}
                </div>
                <BottomNav />
            </div>
        );
    }

    if (!user) return <div className="journey-page"><div className="journey-error">User not found</div></div>;

    const displayIdeas = getDisplayIdeas();
    const displayName = nameOf(user);

    return (
        <div className="journey-page">
            {/* Header: back (left) | title (centre) | share (right) */}
            <div className="journey-header">
                <img src={BackIcon} alt="Back" className="journey-back-btn" onClick={() => navigate(-1)} />
                <h1 className="journey-title">Journey</h1>
                <button
                    type="button"
                    className="journey-share-icon-btn journey-header-share"
                    onClick={handleShare}
                    aria-label="Share profile"
                >
                    <img src={ShareIcon} alt="Share" />
                </button>
            </div>

            {/* Profile Section */}
            <div className="journey-profile-section">
                <div className="journey-profile-row">
                    {profileImageUrl ? (
                        <img src={profileImageUrl} alt="Profile" className="journey-profile-image" />
                    ) : (
                        <div className="journey-profile-avatar">{displayName.charAt(0).toUpperCase()}</div>
                    )}
                    <div className="journey-profile-info">
                        <h2 className="journey-user-name">{displayName}</h2>
                    </div>
                </div>

                <div className="journey-bio-section">
                    <div className="journey-bio-header-row">
                        <h3 className="journey-bio-title">My Story</h3>
                        {currentUser && currentUser.uid === userId && (
                            <button className="journey-bio-edit-btn" onClick={() => isEditingBio ? saveBio() : setIsEditingBio(true)}>
                                {isEditingBio ? 'Save' : 'Edit'}
                            </button>
                        )}
                    </div>
                    {isEditingBio ? (
                        <div className="journey-bio-editor">
                            <textarea className="journey-bio-input" value={bioText} onChange={(e) => e.target.value.length <= 150 && setBioText(e.target.value)} rows={3} />
                            <div className="journey-bio-limit">{bioText.length}/150</div>
                        </div>
                    ) : (
                        <p className="journey-bio-text">{bioText || "No story added yet."}</p>
                    )}
                </div>
            </div>

            {/* Proud Moments — only when users/{userId}.proudMoments is "yes" */}
            {showProud && (
                <div className="journey-profile-section journey-proud-section">
                    <h3 className="journey-bio-title journey-proud-title">Proud Moments</h3>
                    {hasTopMinds ? (
                        <TopMindsBadge />
                    ) : (
                        <p className="journey-proud-empty">No proud moments yet.</p>
                    )}
                </div>
            )}

            {/* TABS */}
            <div className="journey-tabs-header">
                <h3 className="journey-section-title">{displayName.toUpperCase()}'S IDEAS:</h3>
                <div className="journey-tabs">
                    <button className={`journey-tab ${activeTab === 'all' ? 'active' : ''}`} onClick={() => selectTab('all')}>All</button>
                    <button className={`journey-tab ${activeTab === 'impressed' ? 'active' : ''}`} onClick={() => selectTab('impressed')}>Impressed</button>
                    <button className={`journey-tab ${activeTab === 'evolved' ? 'active' : ''}`} onClick={() => selectTab('evolved')}>Evolved</button>
                </div>
            </div>

            {/* CONTENT */}
            {displayIdeas.length === 0 ? (
                <div className="journey-empty-state">
                    {tabLoading[activeTab] ? (
                        <p className="journey-empty-text">Loading...</p>
                    ) : (
                        <>
                            <div className="journey-empty-icon">💡</div>
                            <p className="journey-empty-text">No ideas found</p>
                        </>
                    )}
                </div>
            ) : activeTab === 'all' ? (
                // ---------- ALL: plain paper grid ----------
                <div className="journey-ideas-grid">
                    {displayIdeas.map((idea) => (
                        <div key={idea.id} className="journey-idea-card" onClick={() => handleIdeaClick(idea)}>
                            {(idea.thumbUrl || idea.imageUrl) && (
                                <div className="journey-idea-image-container">
                                    <img src={idea.thumbUrl || idea.imageUrl} alt={idea.title} className="journey-idea-image" loading="lazy" />

                                </div>
                            )}
                            <div className="journey-idea-content">
                                <h4 className="journey-idea-title">{idea.title}</h4>
                                <div className="journey-card-footer">
                                    {idea.tags && (
                                        <div className="journey-idea-tags">
                                            {idea.tags.slice(0, 2).map((tag, i) => (
                                                <span key={i} className="journey-idea-tag">{tag}</span>
                                            ))}
                                        </div>
                                    )}
                                    <div className="journey-stage-icon">
                                        <img src={idea.stageIcon} alt="Stage" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                // ---------- IMPRESSED / EVOLVED: trail with forks ----------
                <div className="journey-trail">
                    {displayIdeas.map((idea) => {
                        const isImpressed = activeTab === 'impressed';
                        const noteText = isImpressed ? idea.latestEvolution?.text : idea.myEvolveText;
                        const evolverName = isImpressed ? idea.latestEvolution?.evolverName : displayName;
                        const evolverImage = isImpressed ? idea.latestEvolution?.evolverImage : profileImageUrl;
                        const moreCount = isImpressed ? Math.max(0, (idea.evolveCount || 1) - 1) : 0;
                        const isExpanded = expandedNoteIds.has(idea.id);
                        const isLong = (noteText || '').length > MAX_NOTE_LENGTH;
                        const displayNote = isLong && !isExpanded
                            ? `${noteText.slice(0, MAX_NOTE_LENGTH).trim()}…`
                            : noteText;

                        return (
                            <div key={idea.id} className="journey-fork-container" onClick={() => handleIdeaClick(idea)}>
                                <span className="journey-trail-marker" aria-hidden="true" />

                                <div className="journey-fork-origin-card">
                                    {(idea.thumbUrl || idea.imageUrl) && <img src={idea.thumbUrl || idea.imageUrl} alt="" className="journey-fork-origin-thumb" loading="lazy" />}
                                    <div className="journey-fork-origin-content">
                                        <h4 className="journey-fork-origin-title">{idea.title}</h4>
                                        {idea.tags && (
                                            <div className="journey-idea-tags">
                                                {idea.tags.slice(0, 2).map((tag, i) => (
                                                    <span key={i} className="journey-idea-tag">{tag}</span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {noteText && (
                                    <div className="journey-fork-branch">
                                        <span className="journey-fork-branch-line" aria-hidden="true" />
                                        <div className="journey-fork-note-card">
                                            <div className="journey-fork-note-header">
                                                <div className="journey-fork-evolver-avatar">
                                                    {evolverImage ? <img src={evolverImage} alt="" /> : (evolverName || '?').charAt(0)}
                                                </div>
                                                <span className="journey-fork-evolver-name">{evolverName}</span>
                                                {moreCount > 0 && (
                                                    <span className="journey-fork-more-badge">+{moreCount} more</span>
                                                )}
                                                <img src={OrbitIcon} alt="" className="journey-fork-stage-badge" />
                                            </div>

                                            <p className="journey-fork-note-text">{displayNote}</p>

                                            {isLong && (
                                                <button
                                                    type="button"
                                                    className="journey-fork-readmore-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleNoteExpanded(idea.id);
                                                    }}
                                                >
                                                    {isExpanded ? 'Show less' : 'Read more'}
                                                </button>
                                            )}

                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <BottomNav />
        </div>
    );
};

export default JourneyPage;